import {
    trackKinds,
    mediaSources,
    videoDimensions,
    mediaStates,
} from 'constants/twilio'

import { getElementByQuerySelector, isElementVisibleInViewport } from './dom'
import { isEmpty, isNil } from './lodash'

const { qcif, vga, HD } = videoDimensions

const checkIfGhost = (name, firstName, lastName) => {
    let isGhost = false
    if (
        name?.endsWith('-whiteboard') ||
        (firstName?.toLowerCase() === 'ghost' &&
            lastName?.toLowerCase() === 'user')
    ) {
        isGhost = true
    }
    return isGhost
}

export const getInfoFromIdentity = identity => {
    const [id, name] = identity.split(':')
    const fullName = name ? name?.split(' ') : []
    const firstName = fullName[0]
    const lastName = fullName[1]
    const isGhost = checkIfGhost(name, firstName, lastName)

    return {
        id,
        name,
        firstName,
        lastName,
        isGhost,
    }
}

export const isVideoTrack = (trackKind, trackName) => {
    return (
        trackKind === trackKinds.video &&
        trackName?.startsWith(mediaSources.camera)
    )
}

export const isScreenShareTrack = (trackKind, trackName) => {
    return trackKind === trackKinds.video && trackName === mediaSources.screen
}

export const isAudioTrack = (trackKind, trackName) => {
    return (
        trackKind === trackKinds.audio &&
        trackName?.startsWith(mediaSources.audio)
    )
}

export const isDataTrack = trackKind => {
    return trackKind === 'data'
}

export const extractAttributesForParticipant = participant => {
    let isVideoEnabled = false
    let isScreenSharing = false
    let isAudioEnabled = false

    const {
        videoTracks,
        audioTracks,
        identity,
        networkQualityLevel,
    } = participant

    if (!isEmpty(videoTracks)) {
        for (let value of videoTracks.values()) {
            const { kind, trackName, isTrackEnabled } = value

            if (isTrackEnabled) {
                if (isVideoTrack(kind, trackName)) {
                    isVideoEnabled = true
                }

                if (isScreenShareTrack(kind, trackName)) {
                    isScreenSharing = true
                }
            }

            // break from loop early if video and screen sharing is already found out enabled
            if (isVideoEnabled && isScreenSharing) break
        }
    }

    if (!isEmpty(audioTracks)) {
        for (let value of audioTracks.values()) {
            const { isTrackEnabled } = value

            if (isTrackEnabled) {
                isAudioEnabled = true
            }

            // break from loop early if audio is already found out enabled
            if (isAudioEnabled) break
        }
    }

    return {
        ...getInfoFromIdentity(identity),
        isVideoEnabled,
        isScreenSharing,
        isAudioEnabled,
        networkQualityLevel,
    }
}

export const isVideoTrackPublicationSubscribed = trackPublication => {
    let isSubscribed = true // fallback value for local video track

    if (
        trackPublication &&
        trackPublication.constructor.name === 'RemoteVideoTrackPublication'
    ) {
        isSubscribed = trackPublication.isSubscribed
    }

    return isSubscribed
}
export const getIdentity = identity => {
    if (!identity) return

    const [uuid, fullName] = identity.split(':')
    const name = fullName ? fullName.split(' ') : []
    let firstName = name[0]
    let lastName = name[1]
    const firstInitial = firstName?.charAt(0)
    const lastInitial = lastName?.charAt(0)
    let initials = firstInitial ? firstInitial : ''
    initials = lastInitial ? `${initials + lastInitial}` : initials
    initials = initials ? initials.toLowerCase() : undefined
    // Tribe Leader is considered a teacher.
    let isTeacher = false
    if (
        firstName?.toLowerCase() === 'tribe' &&
        lastName?.toLowerCase() === 'leader'
    ) {
        isTeacher = true
        firstName = `${firstName} ${lastName}`
        lastName = ''
    }
    // Tribe Leader or Teacher.
    isTeacher = isTeacher || firstName?.toLowerCase() === 'teacher'
    /* Ghost user check */
    let isGhost = checkIfGhost(fullName, firstName, lastName)
    return {
        fullName,
        uuid,
        firstName,
        lastName,
        isTeacher,
        initials,
        isGhost,
    }
}

export const isGhost = participantIdentity =>
    getInfoFromIdentity(participantIdentity).isGhost

export const isStudent = participantIdentity =>
    !getIdentity(participantIdentity).isTeacher &&
    !getIdentity(participantIdentity).isGhost

export const isTeacher = participantIdentity =>
    getIdentity(participantIdentity)?.isTeacher

export const videoConstraints = {
    default: qcif,
    teacher: {
        ...vga,
        frameRate: 20,
    },
    screenshare: {
        ...HD,
        frameRate: 10,
    },
}

export const getParticipantIndex = (participants = [], id = null) => {
    return participants.findIndex(participant => participant.id === id)
}

export const getParticipantSidByIdentity = (identity, isLocal = false) => {
    let participantSid = null

    if (window.twilioRoom) {
        const { localParticipant } = window.twilioRoom

        // first check in local participant
        if (localParticipant.identity.startsWith(identity)) {
            participantSid = localParticipant.sid
        }

        // now check in remote participants if value is nil
        if (isNil(participantSid) && !isLocal) {
            const { participants } = window.twilioRoom

            for (let participant of participants.values()) {
                if (participant.identity.startsWith(identity)) {
                    participantSid = participant.sid
                    break
                }
            }
        }
    }

    return participantSid
}

export const getTeacherSid = (isLocal = false) => {
    let teacherSid = null

    if (window.twilioRoom) {
        const { localParticipant } = window.twilioRoom

        // first check in local participant
        if (isTeacher(localParticipant.identity)) {
            teacherSid = localParticipant.sid
        }

        // now check in remote participants if value is nil
        if (isNil(teacherSid) && !isLocal) {
            const { participants } = window.twilioRoom

            for (let participant of participants.values()) {
                if (isTeacher(participant.identity)) {
                    teacherSid = participant.sid
                    break
                }
            }
        }
    }

    return teacherSid
}

export const getRoomSid = () => {
    let roomSid = null

    if (window.twilioRoom) {
        roomSid = window.twilioRoom.sid
    }

    return roomSid
}

export const switchOffRemoteVideoTrackMedia = videoTrack => {
    if (videoTrack) {
        videoTrack.switchOff()
    }
}

export const switchOnRemoteVideoTrackMedia = videoTrack => {
    if (videoTrack) {
        videoTrack.switchOn()
    }
}

export const toggleRemoteParticipantVideoMedia = (identityId, state) => {
    if (!isEmpty(window.twilioRoom?.participants)) {
        let isToggleDone = false

        for (const participant of window.twilioRoom.participants.values()) {
            if (participant && participant?.identity.startsWith(identityId)) {
                for (const videoTrackPublication of participant.videoTracks.values()) {
                    const { kind, trackName } = videoTrackPublication

                    if (isVideoTrack(kind, trackName)) {
                        if (state === mediaStates.OFF) {
                            switchOffRemoteVideoTrackMedia(
                                videoTrackPublication.track
                            )
                        } else if (state === mediaStates.ON) {
                            switchOnRemoteVideoTrackMedia(
                                videoTrackPublication.track
                            )
                        }

                        isToggleDone = true
                        break
                    }
                }
            }

            if (isToggleDone) {
                break
            }
        }
    }
}

export const toggleVideoTrackMediaOnVisibilityChange = identityId => {
    const videoElement = getElementByQuerySelector(`#tile-${identityId} video`)

    if (videoElement && isElementVisibleInViewport(videoElement)) {
        toggleRemoteParticipantVideoMedia(identityId, mediaStates.ON)
    } else {
        toggleRemoteParticipantVideoMedia(identityId, mediaStates.OFF)
    }
}
